import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AlertCircle, Calendar, CheckCircle, Send, PlayCircle, Inbox } from 'lucide-react';
import Toast, { ToastMessage } from '../../components/common/Toast';
import {
  myWorkService,
  programInstanceService,
  staffService,
  type MyWorkResponse,
  type MyWorkRow,
  type MyWorkBucketName,
  type StaffRoleAssignment,
} from '../../services/auditService';
import apiClient from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import AssignmentModal from '../../components/audits/AssignmentModal';
import { AuditStatusBadge } from './AuditStatusBadge';
import Skeleton from '../../components/common/Skeleton';

/**
 * Plan 05a-02 / STAFF-02 — Auditor work-bucket dashboard.
 *
 * Replaces the prior mock-arrays + rollout/release modal demo with a live
 * data view driven by GET /adt/my-work (extended in Task 1 of this plan
 * with LEFT-JOIN denormalized parent template fields).
 *
 * Layout: 2x2 grid. Four live buckets — Due / Due For Release / Active /
 * My Approvals. The 4th bucket (plan 05b-06 / STAFF-07) surfaces program +
 * procedure instances awaiting the current user's approval; clicking a
 * procedure card navigates to ProcedureInstancePage where ResponseShell
 * renders the Approve / Send Back footer (shipped by 05b-02).
 *
 * Per Decision 2 (05a-CONTEXT):
 *   - Each card shows program_name, procedure_name OR program label,
 *     procedure_type (when row is a procedure), due_date, AuditStatusBadge,
 *     and days_remaining.
 *   - Sort within each bucket: due_date ASC (backend already orders by
 *     due_date NULLS LAST, id; this matches D-02).
 *   - No filters in v1 — buckets ARE the filter.
 *   - Empty-state per bucket: friendly icon + message, no error.
 *   - Loading skeleton per bucket independently.
 */

type BucketKey = MyWorkBucketName | 'my_approvals';

interface BucketDescriptor {
  key: BucketKey;
  label: string;
  icon: React.ReactNode;
  accent: string;    // header dot + border-left accent
  emptyText: string;
  // 05a-02 had a `reserved` flag for the my_approvals placeholder; removed
  // by 05b-06 now that all 4 buckets are live.
}

const BUCKET_DESCRIPTORS: BucketDescriptor[] = [
  {
    key: 'due',
    label: 'Due',
    icon: <AlertCircle size={14} />,
    accent: '#ef4444',
    emptyText: 'Nothing due in the next 7 days — you are caught up.',
  },
  {
    key: 'due_for_release',
    label: 'Due For Release',
    icon: <Send size={14} />,
    accent: '#10b981',
    emptyText: 'No approved programs awaiting release.',
  },
  {
    key: 'active',
    label: 'Active',
    icon: <PlayCircle size={14} />,
    accent: '#0284c7',
    emptyText: 'No active work items.',
  },
  {
    // Plan 05b-06 / STAFF-07 — live approver queue. Was reserved in 05a-02;
    // 05b-06 wires it to data.my_approvals and makes cards navigate to
    // /auditor/procedure-instance/:id where ResponseShell renders the
    // Approve / Send Back footer (shipped by 05b-02).
    key: 'my_approvals',
    label: 'My Approvals',
    icon: <CheckCircle size={14} />,
    accent: '#7c3aed',  // purple — high-visibility approver-action queue
    emptyText: 'Nothing waiting on your approval — great!',
  },
];

function formatDueLabel(row: MyWorkRow, fmtDate: (v: string | null | undefined) => string): string {
  if (row.due_date == null) return 'No due date';
  if (row.days_remaining == null) return fmtDate(row.due_date);
  if (row.days_remaining < 0)  return `Overdue by ${Math.abs(row.days_remaining)}d`;
  if (row.days_remaining === 0) return 'Due today';
  if (row.days_remaining === 1) return 'Due tomorrow';
  return `${row.days_remaining}d remaining`;
}

function rowTitle(row: MyWorkRow): string {
  if (row.instance_type === 'procedure') {
    return row.procedure_name ?? row.label ?? `Procedure #${row.id}`;
  }
  return row.label ?? row.program_name ?? `Program #${row.id}`;
}

function rowSubtitle(row: MyWorkRow): string {
  // Program-name first; for procedure rows include procedure_type alongside.
  const parts: string[] = [];
  if (row.program_name) parts.push(row.program_name);
  if (row.instance_type === 'procedure' && row.procedure_type) {
    parts.push(row.procedure_type);
  }
  return parts.length ? parts.join(' · ') : '—';
}

interface WorkCardProps {
  row: MyWorkRow;
  /** Plan 05a-03 / STAFF-03: when provided, render an Assign button (procedure rows only). */
  onAssign?: (row: MyWorkRow) => void;
  /**
   * Plan 05b-06 / STAFF-07: when provided, the card body is clickable and
   * invokes onOpen(row). The Assign button (if rendered) stops propagation
   * so its click is not swallowed by the card-level navigate.
   */
  onOpen?: (row: MyWorkRow) => void;
}

const WorkCard: React.FC<WorkCardProps> = ({ row, onAssign, onOpen }) => {
  const { formatDate } = useEntitySettings();
  const clickable = typeof onOpen === 'function';
  const cardClasses = [
    'netbody-card',
    'work-card',
    clickable ? 'work-card--clickable' : '',
    row.is_overdue ? 'work-card--overdue' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={cardClasses}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      data-testid={clickable ? `BTN_auditor_dashboard_${row.instance_type}_${row.id}_open` : undefined}
      onClick={clickable ? () => onOpen!(row) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen!(row);
              }
            }
          : undefined
      }
    >
      <div className="work-card-row">
        <span className="work-card-eyebrow">
          {row.instance_type === 'procedure' ? 'Procedure' : 'Program'}
        </span>
        <AuditStatusBadge state={row.workflow_state} size="sm" />
      </div>
      <div className="work-card-title">{rowTitle(row)}</div>
      <div className="work-card-subtitle">{rowSubtitle(row)}</div>
      <div className="work-card-footer">
        <span className="work-card-due">
          <Calendar size={11} />
          {formatDate(row.due_date)}
        </span>
        <span className="work-card-actions">
          <span
            className={`work-card-due-label${row.is_overdue ? ' work-card-due-label--overdue' : ''}`}
          >
            {formatDueLabel(row, formatDate)}
          </span>
          {onAssign && row.instance_type === 'procedure' && (
            <button
              type="button"
              className="work-card-assign-btn"
              data-testid={`BTN_auditor_dashboard_procedure_${row.id}_assign`}
              onClick={(e) => {
                // Plan 05b-06 — stop card-level onOpen from firing when user
                // is targeting the Assign action.
                e.stopPropagation();
                onAssign(row);
              }}
            >
              Assign
            </button>
          )}
        </span>
      </div>
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="work-card work-card-skeleton">
    <div className="work-card-row">
      <Skeleton.Box width="45%" height={11} />
      <Skeleton.Pill width={64} height={18} />
    </div>
    <Skeleton.Box width="70%" height={13} />
    <Skeleton.Box width="50%" height={11} />
    <div className="work-card-footer">
      <Skeleton.Box width={80} height={11} />
      <Skeleton.Box width={60} height={11} />
    </div>
  </div>
);

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <div className="bucket-empty-state">
    <Inbox size={28} className="bucket-empty-icon" />
    <span className="bucket-empty-text">{text}</span>
  </div>
);

interface BucketPanelProps {
  descriptor: BucketDescriptor;
  loading: boolean;
  rows: MyWorkRow[];
  /** Plan 05a-03 / STAFF-03: forwarded to WorkCard. Click handler when the per-row gate passes. */
  onAssign?: (row: MyWorkRow) => void;
  /**
   * Plan 05b-01 / STAFF-08 hardening — per-row predicate; if it returns true for a given row,
   * the card's Assign button is rendered. When omitted (or returns false), the card hides Assign.
   */
  shouldShowAssign?: (row: MyWorkRow) => boolean;
  /**
   * Plan 05b-06 / STAFF-07: card-body click handler — used by my_approvals to
   * navigate to /auditor/procedure-instance/:id. Forwarded to WorkCard.
   */
  onOpen?: (row: MyWorkRow) => void;
}

const BucketPanel: React.FC<BucketPanelProps> = ({
  descriptor, loading, rows, onAssign, shouldShowAssign, onOpen,
}) => {
  const showEmpty = !loading && rows.length === 0;
  return (
    <div
      className="netbody-card bucket-panel"
      style={{ ['--accent' as any]: descriptor.accent }}
    >
      <div className="netbody-header bucket-panel-header">
        <span className="bucket-panel-icon">{descriptor.icon}</span>
        <span className="netbody-header-title bucket-panel-title">
          {descriptor.label}
        </span>
        <span className="bucket-panel-count">{loading ? '…' : rows.length}</span>
      </div>
      <div className="scroll-container bucket-panel-scroll">
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : showEmpty ? (
          <EmptyState text={descriptor.emptyText} />
        ) : (
          rows.map((row) => (
            <WorkCard
              key={`${row.instance_type}-${row.id}`}
              row={row}
              onAssign={shouldShowAssign && shouldShowAssign(row) ? onAssign : undefined}
              onOpen={onOpen}
            />
          ))
        )}
      </div>
    </div>
  );
};

/**
 * Plan 05a-03 / STAFF-03 — Assign-button gating.
 *
 * Resolve the parent program's agency_id for a procedure (or program) row so
 * the AssignmentModal can scope the staff picker. Cheap GET against the
 * existing /adt/program_instances/{id} endpoint; failure is swallowed (modal
 * just shows "Loading staff…" until cancelled).
 */
async function resolveAgencyIdForRow(row: MyWorkRow): Promise<number | null> {
  const pid =
    row.parent_program_instance_id ??
    (row.instance_type === 'program' ? row.id : null);
  if (!pid) return null;
  try {
    const resp = await apiClient.get(`/api/v1/adt/program_instances/${pid}`);
    const ag = (resp.data?.agency_id ?? null) as number | null;
    return ag;
  } catch {
    return null;
  }
}

export default function AuditorDashboardContent() {
  const { state } = useAuth();
  const currentUserId = state.user?.id ?? null;
  // Plan 05b-06 / STAFF-07 — used by my_approvals card click to land on the
  // ProcedureInstancePage where ResponseShell renders the Approve/Send Back footer.
  const navigate = useNavigate();

  const [data, setData] = useState<MyWorkResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  // Plan 05b-01 / STAFF-08 hardening — per-agency Set instead of single boolean.
  // Built once on mount via the same staffService walk previously used for the
  // boolean. An entry in this set means the user has at least one active
  // Program Head StaffRole on that agency.
  const [ledAgencyIds, setLedAgencyIds] = useState<Set<number>>(() => new Set());

  // Per-row agency resolution cache (procedure parent_program_instance_id -> agency_id | null).
  // Keeps the existing single-fetch model: each program is resolved at most once.
  const agencyByProgramRef = React.useRef<Map<number, number | null>>(new Map());
  const [agencyResolveTick, setAgencyResolveTick] = useState(0);  // bump to re-render after a resolve

  // Plan 05a-03 — modal state (which row is being assigned, plus the lazily
  // resolved agency_id used to scope the staff picker).
  const [assigningRow, setAssigningRow] = useState<MyWorkRow | null>(null);
  const [assigningAgencyId, setAssigningAgencyId] = useState<number | null>(null);

  // Plan 05a-03 — extracted into a useCallback so the modal's onSaved can
  // re-run the same /adt/my-work fetch after a successful PUT.
  const refetch = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    myWorkService
      .get({})
      .then((resp) => {
        if (cancelled) return;
        setData(resp);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err?.response?.data?.detail ??
          err?.message ??
          'Failed to load your work — please refresh.';
        setToast({ type: 'error', message: typeof msg === 'string' ? msg : 'Failed to load your work — please refresh.' });
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cleanup = refetch();
    return cleanup;
  }, [refetch]);

  // Plan 05b-01 / STAFF-08 hardening — fire-and-forget per-agency Program-Head
  // probe. Walks all active staff memberships for the current user and collects
  // every agency on which they hold an active 'Program Head' role.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    (async () => {
      try {
        const allStaff = await staffService.list();
        const mine = allStaff.filter(
          (s) => s.user_id === currentUserId && (s.is_active ?? 1) === 1,
        );
        const acc = new Set<number>();
        for (const m of mine) {
          try {
            const roles = await staffService.getRoles(m.member_id);
            for (const r of roles as StaffRoleAssignment[]) {
              if (r.role_name === 'Program Head' && (r.is_active ?? 1) === 1) {
                acc.add(r.agency_id);
              }
            }
          } catch {
            // Per-membership probe failed — skip that membership.
          }
        }
        if (!cancelled) setLedAgencyIds(acc);
      } catch {
        // Silent — Assign just stays hidden.
      }
    })();
    return () => { cancelled = true; };
  }, [currentUserId]);

  // Plan 05b-01 — per-row predicate. Resolves the row's parent-program agency
  // exactly once (cached in a ref), then returns whether the user holds an
  // active Program Head role on that agency. While the cache fills, returns
  // false so the button stays hidden (no flicker).
  const isProgramHeadForRow = useCallback(
    (row: MyWorkRow): boolean => {
      if (row.instance_type !== 'procedure') return false;
      const pid = row.parent_program_instance_id;
      if (pid == null) return false;
      const cached = agencyByProgramRef.current.get(pid);
      if (cached === undefined) {
        // Fire-and-forget resolve; do not block render. When it lands, bump tick.
        (async () => {
          try {
            const resp = await apiClient.get(`/api/v1/adt/program_instances/${pid}`);
            const ag = (resp.data?.agency_id ?? resp.data?.owner_agency_id ?? null) as number | null;
            agencyByProgramRef.current.set(pid, ag);
            setAgencyResolveTick((t) => t + 1);
          } catch {
            agencyByProgramRef.current.set(pid, null);
            setAgencyResolveTick((t) => t + 1);
          }
        })();
        return false;  // hide button until cache fills
      }
      if (cached === null) return false;
      return ledAgencyIds.has(cached);
    },
    // agencyResolveTick is intentionally a dependency so callers re-evaluate
    // after the cache fills, even though the function body reads from a ref.
    [ledAgencyIds, agencyResolveTick],
  );

  const rowsByBucket = useMemo<Record<BucketKey, MyWorkRow[]>>(() => {
    return {
      due: data?.due ?? [],
      due_for_release: data?.due_for_release ?? [],
      active: data?.active ?? [],
      // Plan 05b-06 / STAFF-07 — was hard-coded [] when my_approvals was reserved.
      my_approvals: data?.my_approvals ?? [],
      my_approved: data?.my_approved ?? [],
    };
  }, [data]);

  // Plan 05b-06 / STAFF-07 — card-click handler for the my_approvals bucket.
  // Procedure rows land on ProcedureInstancePage (shipped by 05b-02) where
  // ResponseShell renders the Approve / Send Back footer. Program rows are
  // a TODO — no /auditor/program-instance/:id approval page exists yet.
  const handleApprovalOpen = useCallback(
    (row: MyWorkRow) => {
      if (row.instance_type === 'procedure') {
        navigate(`/procedure-instance/${row.id}`);
        return;
      }
      // TODO program-instance approval page — no route shipped yet.
      // For now, ignore the click on program-row approval cards.
    },
    [navigate],
  );

  // 2026-05-18 (migration 081) — due_for_release bucket card click → release
  // the audit report. Findings then become visible to the auditee side.
  // Backend enforces approver / Agency Head gate; this is just the trigger.
  const handleReleaseOpen = useCallback(
    async (row: MyWorkRow) => {
      if (row.instance_type !== 'program') {
        // Procedure-level cards in the due_for_release bucket are not
        // releaseable on their own — release is at the program level.
        return;
      }
      const programName = row.program_name ?? row.label ?? `Program #${row.id}`;
      const ok = window.confirm(
        `Release audit report for "${programName}"?\n\n` +
          `Findings on this program will become visible to the auditee side ` +
          `(GRC team / response users). This action is one-shot — once released, ` +
          `the report cannot be un-released through the UI.`,
      );
      if (!ok) return;
      try {
        await programInstanceService.release(row.id);
        refetch();
      } catch (e: any) {
        const msg = e?.response?.data?.detail ?? e?.message ?? 'Failed to release report.';
        window.alert(`Release failed: ${msg}`);
      }
    },
    [refetch],
  );

  const handleAssignClick = useCallback(async (row: MyWorkRow) => {
    setAssigningRow(row);
    setAssigningAgencyId(null);
    const ag = await resolveAgencyIdForRow(row);
    setAssigningAgencyId(ag);
  }, []);

  const handleAssignmentClosed = useCallback(() => {
    setAssigningRow(null);
    setAssigningAgencyId(null);
  }, []);

  const handleAssignmentSaved = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div className="auditor-dashboard-root">
      <div className="auditor-dashboard-grid-wrap">
        <div className="auditor-dashboard-grid">
          {BUCKET_DESCRIPTORS.map((d) => (
            <BucketPanel
              key={d.key}
              descriptor={d}
              loading={loading}
              rows={rowsByBucket[d.key]}
              onAssign={handleAssignClick}
              shouldShowAssign={isProgramHeadForRow}
              onOpen={d.key === 'due_for_release' ? handleReleaseOpen : handleApprovalOpen}
            />
          ))}
        </div>
      </div>

      {currentUserId != null && (
        <AssignmentModal
          isOpen={assigningRow !== null}
          onClose={handleAssignmentClosed}
          row={assigningRow}
          agencyId={assigningAgencyId}
          currentUserId={currentUserId}
          onSaved={handleAssignmentSaved}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
